import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { setActingCustomer } from '@/lib/actingContext';

export interface Membership {
  customerId: string;
  customerName: string;
  customerActive: boolean;
  role: string;
  defaultLocationId: string | null;
  membershipActive: boolean;
  isActive: boolean;
  isFavorite: boolean;
  /**
   * This person works the WHOLE chain from this account — chain-wide surfaces
   * (invoice list, merged statement, pay run, equipment/service/cylinders)
   * light up there. Only ever true on a membership to a group master.
   *
   * ⚠ A user can hold this on SEVERAL chains at once (Scott is AP on both
   * HOMEROOM and THE MELT MAIN). Anything reading it must handle N, not one —
   * the switcher used to `.find()` a single one and rename that row "All
   * stores", which relabelled one chain and left the other looking different
   * for no reason.
   */
  masterAccounting: boolean;
  /** This account is a chain / franchise master (it has stores under it). */
  isGroupMaster: boolean;
  groupBrand: string | null;
}

interface MembershipDbRow {
  customer_id: string;
  customer_name: string;
  customer_active: boolean;
  role: string;
  default_location_id: string | null;
  membership_active: boolean;
  is_active: boolean;
  is_favorite: boolean;
  master_accounting: boolean | null;
  is_group_master: boolean | null;
  group_brand: string | null;
}

/**
 * Lists customers the signed-in user can quick-switch to: their active
 * memberships plus (for superadmins) explicitly FAVORITED customers and
 * whatever customer they're currently acting as. Acting as an arbitrary
 * customer happens from the admin portal ("Act as customer" on the customer
 * detail page) — the sidebar switcher is just the quick list.
 */
export function useMyMemberships() {
  return useQuery<Membership[]>({
    queryKey: ['my-memberships'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('my_memberships')
        .select('*');
      if (error) return [];
      return (data ?? []).map((r): Membership => {
        const row = r as MembershipDbRow;
        return {
          customerId: row.customer_id,
          customerName: row.customer_name,
          customerActive: row.customer_active,
          role: row.role,
          defaultLocationId: row.default_location_id,
          membershipActive: row.membership_active,
          isActive: row.is_active,
          isFavorite: row.is_favorite ?? false,
          masterAccounting: row.master_accounting === true,
          isGroupMaster: row.is_group_master === true,
          groupBrand: row.group_brand ?? null,
        };
      });
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Switches the signed-in user's active customer. Invalidates every query
 * keyed off customer data so the app reloads under the new context.
 */
export function useSetActiveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string) => {
      // Set this tab's context immediately so RLS + the app agree, then
      // persist to the column too (keeps my_memberships' "current" marker and
      // fresh-tab defaults consistent).
      setActingCustomer(customerId);
      const { data, error } = await supabase
        .schema('orders')
        .rpc('set_active_customer', { p_customer_id: customerId });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries();
    },
  });
}

/**
 * Clears the signed-in user's acting-as context (active_customer_id → NULL).
 * The "Exit" affordance on the Acting-as bar: a superadmin drops back to no
 * customer context so no order/payment can accidentally land on a customer
 * they forgot they were acting as. Invalidates every query so the app leaves
 * the customer context immediately.
 */
export function useClearActiveCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Clear this tab's context AND the persisted column, so nothing sticky
      // is left behind on either.
      setActingCustomer(null);
      const { error } = await supabase.schema('orders').rpc('clear_active_customer');
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries();
    },
  });
}

/**
 * Adds/removes a customer on the caller's personal favorites quick list
 * (orders.customer_users.favorite_customer_ids via set_customer_favorite).
 * Favorited customers appear in the sidebar account switcher for superadmins.
 */
export function useSetCustomerFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { customerId: string; favorite: boolean }) => {
      const { data, error } = await supabase
        .schema('orders')
        .rpc('set_customer_favorite', {
          p_customer_id: input.customerId,
          p_favorite: input.favorite,
        });
      if (error) throw error;
      return data as string[];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-memberships'] });
    },
  });
}
